import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { PunchHistoryService } from '../../services/punch-history.service';
import { GetPunchHistoryParams, PunchHistoryLog } from '../../models/punch-history.model';
import { PunchesNavComponent } from '../../../shared/punches-nav/punches-nav.component';

dayjs.extend(utc);

@Component({
  selector: 'app-punch-history',
  standalone: true,
  imports: [CommonModule, FormsModule, PunchesNavComponent],
  templateUrl: './punch-history.component.html',
  styleUrls: ['./punch-history.component.scss'],
})
export class PunchHistoryComponent implements OnInit {
  punchLogs: PunchHistoryLog[] = [];
  expandedLogIds = new Set<number>();

  isLoading = false;
  isExporting = false;
  errorMessage: string | null = null;

  currentPage = 1;
  pageSize = 25;
  totalPages = 1;
  totalCount = 0;
  readonly pageSizeOptions = [10, 25, 50, 100];

  searchTerm = '';

  constructor(
    private readonly punchHistoryService: PunchHistoryService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadPunchHistory();
  }

  private buildPunchHistoryParams(
    overrides: Partial<GetPunchHistoryParams> = {},
  ): GetPunchHistoryParams {
    return {
      pageNumber: overrides.pageNumber ?? this.currentPage,
      pageSize: overrides.pageSize ?? this.pageSize,
      searchTerm: this.searchTerm.trim() || null,
      orderBy: 'logTimeDesc',
      ...overrides,
    };
  }

  async loadPunchHistory(page: number = 1): Promise<void> {
    this.isLoading = true;
    this.errorMessage = null;
    this.cdr.detectChanges();

    try {
      const res = await firstValueFrom(
        this.punchHistoryService.getPunchHistory(
          this.buildPunchHistoryParams({
            pageNumber: page,
            pageSize: this.pageSize,
          }),
        ),
      );

      this.punchLogs = res.data ?? [];
      this.totalPages = res.totalPages || 1;
      this.totalCount = res.totalCount || 0;
      this.currentPage = res.currentPage || 1;

      const validIds = new Set(this.punchLogs.map((log) => log.id));
      this.expandedLogIds.forEach((id) => {
        if (!validIds.has(id)) {
          this.expandedLogIds.delete(id);
        }
      });
    } catch (err) {
      console.error('Failed to load punch history:', err);
      this.errorMessage = 'Failed to load punch history';
      this.punchLogs = [];
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  async onSearch(): Promise<void> {
    await this.loadPunchHistory(1);
  }

  async clearSearch(): Promise<void> {
    this.searchTerm = '';
    await this.loadPunchHistory(1);
  }

  async exportToExcel(): Promise<void> {
    if (this.isExporting) {
      return;
    }

    this.isExporting = true;
    this.cdr.detectChanges();

    try {
      const exportPageSize = 500;
      const firstPage = await firstValueFrom(
        this.punchHistoryService.getPunchHistory(
          this.buildPunchHistoryParams({
            pageNumber: 1,
            pageSize: exportPageSize,
          }),
        ),
      );

      const allLogs: PunchHistoryLog[] = [...(firstPage.data ?? [])];
      const totalPages = firstPage.totalPages ?? 1;

      for (let page = 2; page <= totalPages; page++) {
        const pageRes = await firstValueFrom(
          this.punchHistoryService.getPunchHistory(
            this.buildPunchHistoryParams({
              pageNumber: page,
              pageSize: exportPageSize,
            }),
          ),
        );

        allLogs.push(...(pageRes.data ?? []));
      }

      const rows = allLogs.map((log) => ({
        'Employee Name': this.getUserFullName(log),
        'Employee Number': log.user?.employeeNumber?.trim() || '—',
        RFID: log.user?.rfid?.trim() || '—',
        'Clock Device': this.getClockDeviceName(log),
        'Log Type': this.getLogTypeLabel(log),
        Violation: this.getViolationText(log),
        'Display Date': this.formatLogDate(log.logTime),
        'Display Time': this.formatLogTime(log.logTime),
        'Display Timestamp': this.formatLogDateTime(log.logTime),
        'Raw Log Timestamp': this.formatLogFullTimestamp(log.logTime),
        'Rounded Log Timestamp': this.formatLogFullTimestamp(log.roundedLogTime),
        'Updated At': this.formatLogDateTime(log.updatedAt),
        'Updated By': this.getUpdatedByName(log),
        'Log ID': log.id,
      }));

      const XLSX = await import('xlsx');
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Punch History');

      const timestamp = dayjs().format('YYYY-MM-DD_HH-mm');
      const searchSuffix = this.searchTerm.trim()
        ? `_${this.searchTerm.trim().replace(/[^a-zA-Z0-9-_]+/g, '_')}`
        : '';

      XLSX.writeFile(workbook, `punch-history${searchSuffix}_${timestamp}.xlsx`);
    } catch (err) {
      console.error('Failed to export punch history:', err);
      this.errorMessage = 'Failed to export punch history';
    } finally {
      this.isExporting = false;
      this.cdr.detectChanges();
    }
  }

  toggleExpanded(logId: number): void {
    if (this.expandedLogIds.has(logId)) {
      this.expandedLogIds.delete(logId);
      return;
    }

    this.expandedLogIds.add(logId);
  }

  isExpanded(logId: number): boolean {
    return this.expandedLogIds.has(logId);
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      void this.loadPunchHistory(this.currentPage - 1);
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      void this.loadPunchHistory(this.currentPage + 1);
    }
  }

  changePageSize(): void {
    void this.loadPunchHistory(1);
  }

  trackByLogId(_: number, item: PunchHistoryLog): number {
    return item.id;
  }

  getUserFullName(log: PunchHistoryLog): string {
    if (!log.user) {
      return '—';
    }

    const fullName = `${log.user.firstName ?? ''} ${log.user.lastName ?? ''}`.trim();
    return fullName || '—';
  }

  getClockDeviceName(log: PunchHistoryLog): string {
    return log.clockDevice?.name?.trim() || 'Manuel Punch';
  }

  getUpdatedByName(log: PunchHistoryLog): string {
    if (!log.updatedBy) {
      return '—';
    }

    const fullName = `${log.updatedBy.firstName ?? ''} ${log.updatedBy.lastName ?? ''}`.trim();
    return fullName || '—';
  }

  getViolationText(log: PunchHistoryLog): string {
    return log.violation?.type?.trim() || '—';
  }

  hasViolation(log: PunchHistoryLog): boolean {
    return !!log.violation?.type?.trim();
  }

  getLogTypeLabel(log: PunchHistoryLog): string {
    if (log.logType === 'ClockIn') {
      return 'In';
    }

    if (log.logType === 'ClockOut') {
      return 'Out';
    }

    return log.logType || '—';
  }

  getLogTypeClass(log: PunchHistoryLog): string {
    const normalized = String(log.logType ?? '')
      .trim()
      .toLowerCase();

    if (normalized === 'clockin') {
      return 'clockin';
    }

    if (normalized === 'clockout') {
      return 'clockout';
    }

    return 'invalid';
  }

  getDisplayLogTime(log: PunchHistoryLog): string {
    return log.roundedLogTime ?? log.logTime;
  }

  private parseUtcToLocal(value: string | null | undefined): dayjs.Dayjs | null {
    if (!value?.trim()) {
      return null;
    }

    const parsed = dayjs.utc(value.trim());

    if (!parsed.isValid()) {
      return null;
    }

    return parsed.local();
  }

  formatLogDate(value: string | null | undefined): string {
    const parsed = this.parseUtcToLocal(value);
    return parsed ? parsed.format('MMM D, YYYY') : '—';
  }

  formatLogTime(value: string | null | undefined): string {
    const parsed = this.parseUtcToLocal(value);
    return parsed ? parsed.format('h:mm A') : '—';
  }

  formatLogDateTime(value: string | null | undefined): string {
    const parsed = this.parseUtcToLocal(value);
    return parsed ? parsed.format('MMM D, YYYY h:mm A') : '—';
  }

  formatLogFullTimestamp(value: string | null | undefined): string {
    const parsed = this.parseUtcToLocal(value);
    return parsed ? parsed.format('MMM D, YYYY h:mm:ss A') : '—';
  }
}
